import React, { useState, useEffect, useCallback } from "react";
import { Card, ListGroup } from "react-bootstrap";
import { collection, getDocs, limit, orderBy, query, where, Timestamp } from "@firebase/firestore";
import { db } from "../../firebase/FirebaseProvider";
import { Photo, User, Session } from "../types/Types";
import { NOTIFICATION_LIMIT, logDataLoaded } from "../util/Util";
import PhotoAddedRow from "./photos/PhotoAddedRow";
import PhotoLikedRow from "./photos/PhotoLikedRow";
import { usePagination } from "../../hooks/usePagination";
import PaginationControls from "../shared/PaginationControls";

interface Like {
    id: string;
    userId: string;
    userName: string;
    likeType: string;
    entityId: string;
    locationId: string;
    locationName: string;
    createdAt: Date;
}

interface Props {
    usersMap: Map<string, User>;
    sessionsMap: Map<string, Session>;
    isLoading?: boolean;
}

type CombinedItem = (Photo & { type: 'photo' }) | (Like & { type: 'like' });

const PhotoHistory: React.FC<Props & { photos: Photo[] }> = ({ usersMap, sessionsMap, isLoading = false, photos }) => {
    const [likes, setLikes] = useState<Like[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const { pageStart, pageEnd, pageLeft, pageRight, resetPagination, getPaginatedItems } = usePagination<CombinedItem>({ pageSize: 50 });

    // Load likes as before, but keep photos as prop
    const loadRecentLikes = useCallback(async () => {
        try {
            setLoading(true);
            // Calculate date 15 days ago
            const photoHistoryCutoff = new Date();
            photoHistoryCutoff.setDate(photoHistoryCutoff.getDate() - 15);

            const likesQuery = query(
                collection(db, 'likes'),
                where('likeType', '==', 'PHOTO'),
                where('createdAt', '>=', Timestamp.fromDate(photoHistoryCutoff)),
                orderBy('createdAt', 'desc'),
                limit(NOTIFICATION_LIMIT)
            );
            const likesSnapshot = await getDocs(likesQuery);
            const likesData: Like[] = [];
            likesSnapshot.forEach((docSnap) => {
                const data = docSnap.data();
                const like: Like = {
                    id: docSnap.id,
                    userId: data.userId,
                    userName: data.userName,
                    likeType: data.likeType,
                    entityId: data.entityId,
                    locationId: data.locationId,
                    locationName: data.locationName,
                    createdAt: data.createdAt,
                };
                likesData.push(like);
            });

            logDataLoaded('photo history likes', likesData.length);
            setLikes(likesData);
        } catch (error) {
            console.error("Error loading recent likes:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadRecentLikes();
    }, [loadRecentLikes]);

    useEffect(() => {
        resetPagination();
    }, [photos, likes, resetPagination]);

    if (loading || isLoading) {
        return (
            <Card className={"mb-3"}>
                <Card.Header>
                    <div><b>Photo History</b></div>
                </Card.Header>
                <ListGroup variant="flush">
                    <div className={'loading'}>Loading photo history...</div>
                </ListGroup>
            </Card>
        );
    }

    if (photos.length === 0) {
        return (
            <Card className={"mb-3"}>
                <Card.Header>
                    <div><b>Photo History</b></div>
                </Card.Header>
                <ListGroup variant="flush">
                    <div className={'loading'}>No photos yet</div>
                </ListGroup>
            </Card>
        );
    }

    const itemsToDisplay: CombinedItem[] = [
        ...photos.map(photo => ({ ...photo, type: 'photo' as const })),
        ...likes.map(like => ({ ...like, type: 'like' as const }))
    ].sort((a, b) => {
        return (b.createdAt as unknown as Timestamp).seconds - (a.createdAt as unknown as Timestamp).seconds;
    });

    const paginatedItems = getPaginatedItems(itemsToDisplay);

    return (
        <Card className={"mb-3"}>
            <Card.Header>
                <div><b>Photo History</b></div>
            </Card.Header>
            <ListGroup variant="flush">
                {paginatedItems.map((item, index) => (   
                    item.type === 'photo' ? (
                        <PhotoAddedRow
                            key={item.id}
                            photo={item}
                            usersMap={usersMap}
                            sessionsMap={sessionsMap}
                            notificationLimit={NOTIFICATION_LIMIT}
                        />
                    ) : (
                        <PhotoLikedRow
                            key={item.id}
                            like={item}
                            usersMap={usersMap}
                            sessionsMap={sessionsMap}
                        />
                    )
                ))}
            </ListGroup>
            <Card.Footer>
                <PaginationControls
                    pageStart={pageStart}
                    pageEnd={pageEnd}
                    totalItems={itemsToDisplay.length}
                    onPageLeft={pageLeft}
                    onPageRight={() => pageRight(itemsToDisplay.length)}
                />
            </Card.Footer>
        </Card>
    );
};

export default PhotoHistory;
